import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import { userApi, ApiError } from '../../../services/api';
import styles from './ProfileNotificationsSettingScreen.module.css';

interface NotificationSettings {
  email: {
    appointments: boolean;
    claimsUpdates: boolean;
    promotions: boolean;
    healthTips: boolean;
    accountAlerts: boolean;
  };
  sms: {
    appointments: boolean;
    claimsUpdates: boolean;
    urgentAlerts: boolean;
  };
  push: {
    appointments: boolean;
    claimsUpdates: boolean;
    promotions: boolean;
    healthTips: boolean;
    nearbyProviders: boolean;
  };
}

interface NotificationCategory {
  id: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  channels: ('email' | 'sms' | 'push')[];
}

const ProfileNotificationsSettingScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<NotificationSettings>({
    email: {
      appointments: true,
      claimsUpdates: true,
      promotions: false,
      healthTips: true,
      accountAlerts: true
    },
    sms: {
      appointments: true,
      claimsUpdates: false,
      urgentAlerts: true
    },
    push: {
      appointments: true,
      claimsUpdates: true,
      promotions: false,
      healthTips: false,
      nearbyProviders: true
    }
  });
  const [originalSettings, setOriginalSettings] = useState<NotificationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const notificationCategories: NotificationCategory[] = [
    {
      id: 'appointments',
      label: 'Appointment Reminders',
      description: 'Get notified about upcoming appointments and schedule changes',
      icon: <EventIcon />,
      channels: ['email', 'sms', 'push']
    },
    {
      id: 'claimsUpdates',
      label: 'Claims Updates',
      description: 'Receive updates on claim status and processing',
      icon: <HealthAndSafetyIcon />,
      channels: ['email', 'sms', 'push']
    },
    {
      id: 'promotions',
      label: 'Promotions & Offers',
      description: 'Special offers and discounts from healthcare providers',
      icon: <LocalOfferIcon />,
      channels: ['email', 'push']
    },
    {
      id: 'healthTips',
      label: 'Health Tips',
      description: 'Personalized health tips and wellness content',
      icon: <InfoIcon />,
      channels: ['email', 'push']
    },
    {
      id: 'accountAlerts',
      label: 'Account Security',
      description: 'Important account and security notifications',
      icon: <NotificationsIcon />,
      channels: ['email']
    },
    {
      id: 'urgentAlerts',
      label: 'Urgent Alerts',
      description: 'Critical and time-sensitive notifications only',
      icon: <NotificationsIcon />,
      channels: ['sms']
    },
    {
      id: 'nearbyProviders',
      label: 'Nearby Providers',
      description: 'Notifications when new providers are available in your area',
      icon: <NotificationsIcon />,
      channels: ['push']
    }
  ];

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      const result = await userApi.getNotificationSettings();
      setSettings(result);
      setOriginalSettings(result);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (channel: keyof NotificationSettings, setting: string) => {
    setSettings(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [setting]: !prev[channel][setting as keyof typeof prev[typeof channel]]
      }
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await userApi.updateNotificationSettings(settings);
      
      setOriginalSettings(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving notification settings:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <EmailIcon className={styles.channelIcon} />;
      case 'sms':
        return <SmsIcon className={styles.channelIcon} />;
      case 'push':
        return <NotificationsIcon className={styles.channelIcon} />;
      default:
        return null;
    }
  };

  const isSettingEnabled = (channel: keyof NotificationSettings, settingId: string): boolean => {
    const channelSettings = settings[channel];
    return channelSettings[settingId as keyof typeof channelSettings] || false;
  };

  if (isLoading) {
    return (
      <div className={styles.profileNotificationsSetting}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileNotificationsSetting}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Notification Settings</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        <div className={styles.channelSection}>
          <h2 className={styles.sectionTitle}>Notification Channels</h2>
          <div className={styles.channelCards}>
            <div className={styles.channelCard}>
              <EmailIcon className={styles.channelMainIcon} />
              <h3 className={styles.channelName}>Email</h3>
              <p className={styles.channelDescription}>
                Receive notifications in your email inbox
              </p>
            </div>
            <div className={styles.channelCard}>
              <SmsIcon className={styles.channelMainIcon} />
              <h3 className={styles.channelName}>SMS</h3>
              <p className={styles.channelDescription}>
                Get text messages for important updates
              </p>
            </div>
            <div className={styles.channelCard}>
              <NotificationsIcon className={styles.channelMainIcon} />
              <h3 className={styles.channelName}>Push</h3>
              <p className={styles.channelDescription}>
                Real-time notifications on your device
              </p>
            </div>
          </div>
        </div>

        <div className={styles.categoriesSection}>
          <h2 className={styles.sectionTitle}>Notification Preferences</h2>
          {notificationCategories.map(category => (
            <div key={category.id} className={styles.categoryCard}>
              <div className={styles.categoryHeader}>
                <div className={styles.categoryIcon}>{category.icon}</div>
                <div className={styles.categoryInfo}>
                  <h3 className={styles.categoryLabel}>{category.label}</h3>
                  <p className={styles.categoryDescription}>{category.description}</p>
                </div>
              </div>
              <div className={styles.channelToggles}>
                {category.channels.map(channel => (
                  <div key={`${category.id}-${channel}`} className={styles.toggleItem}>
                    {getChannelIcon(channel)}
                    <Switch
                      checked={isSettingEnabled(channel, category.id)}
                      onChange={() => handleToggle(channel, category.id)}
                      color="primary"
                      size="small"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {saveSuccess && (
          <div className={styles.successMessage}>
            <CheckCircleIcon className={styles.successIcon} />
            Notification settings updated successfully!
          </div>
        )}

        <div className={styles.actionSection}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!hasChanges() || isSaving}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        <div className={styles.infoSection}>
          <h3 className={styles.infoTitle}>About Notifications</h3>
          <p className={styles.infoText}>
            We'll only send you notifications for the categories you've enabled. You can change these settings at any time.
          </p>
          <p className={styles.infoText}>
            <strong>Email:</strong> Detailed updates and non-urgent communications
          </p>
          <p className={styles.infoText}>
            <strong>SMS:</strong> Time-sensitive alerts and appointment reminders
          </p>
          <p className={styles.infoText}>
            <strong>Push:</strong> Real-time updates when using the app
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileNotificationsSettingScreen;